import { useEffect } from 'react';
import Layout from '../../../components/layout/Layout';
import Breadcrumb from '../../../components/layout/Breadcrumb';
import { updateSEO } from '../../../utils/seo';

export default function ButlerPageEN() {
  useEffect(() => {
    updateSEO({
      title: 'What Is a Butler | Professional Definition & Standards | Japan Butler Association',
      description:
        'How the Japan Butler Association defines the butler profession: scope of responsibility, boundaries with related roles, Associate, Professional, and Master attainment levels, and the profession in Japan today.',
      keywords:
        'what is a butler, butler profession, butler definition, Associate Professional Master Butler, Japan Butler Association',
      canonical: 'https://butler-association.jp/en/butler',
    });
  }, []);

  const breadcrumbs = [
    { label: 'Home', href: '/en' },
    { label: 'What Is a Butler' },
  ];

  const BTN08_ON_LIGHT =
    "relative inline-flex items-center justify-center bg-[#1a2942] text-[#f8f6f0] border border-[#a38e65] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Playfair_Display',serif] transition-colors duration-300 hover:bg-[#a38e65] hover:text-[#1a2942] hover:border-[#1a2942] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#a38e65]/45 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#1a2942]/45";
  const BTN08_ON_NAVY =
    "relative inline-flex items-center justify-center bg-[#a38e65] text-white font-semibold border border-[#1a2942] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Playfair_Display',serif] transition-colors duration-300 hover:bg-[#1a2942] hover:text-[#f8f6f0] hover:border-[#a38e65] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#1a2942]/40 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#a38e65]/50";

  const responsibilityAreas = [
    {
      no: '01',
      en: 'Household Continuity',
      title: 'Continuity of household operations',
      desc: 'Daily sequencing, guest reception, inventory awareness, and coordination with outside vendors—building the foundation so life runs without friction. The work is not a string of one-off tasks; it requires a view of the household’s overall rhythm.',
    },
    {
      no: '02',
      en: 'Discretion & Trust',
      title: 'Discretion and the keeping of trust',
      desc: 'Because the role enters the private interior of a household, silence about what is seen and heard, and care in every judgment, are indispensable. Trust is a precondition of skill, and it sits at the core of how attainment is assessed.',
    },
    {
      no: '03',
      en: 'Anticipatory Service',
      title: 'Anticipation and quiet arrangement',
      desc: 'The ability to read needs before they are spoken and to turn them into arrangements that are neither excessive nor incomplete.\n\nUnobtrusive yet reliable support—the “invisible service”—is what the Association regards as an essential butler responsibility.',
    },
    {
      no: '04',
      en: 'Protocol & Etiquette',
      title: 'Protocol and interpersonal form',
      desc: 'Managing manners and flow for guests, ceremonies, and dining according to the occasion. Judgment in applying Western discipline and Japanese courtesy as the situation requires is a mark of professional depth.',
    },
  ];

  const boundaries = [
    {
      role: 'Housekeeper / Domestic Help',
      en: 'Housekeeping',
      point: 'Primary focus: executing tasks',
      text: 'This role centers on carrying out discrete domestic work—cleaning, laundry, cooking—with reliability.\n\nA butler may take part in household tasks, yet the emphasis is on designing what is done, in what order, and by whom, so that the quality of the whole household is sustained.\n\nMoving one’s hands on site and overseeing household operations sit on different layers of responsibility.',
    },
    {
      role: 'Secretary',
      en: 'Secretary',
      point: 'Primary focus: business schedule and documents',
      text: 'A secretary supports the principal’s professional calendar, correspondence, and documents. A butler’s center of gravity is the private side of life—household, guests, travel, and personal surroundings.\n\nOverlap exists, yet the professions diverge by whether the main field of responsibility is the office or daily living.',
    },
    {
      role: 'Concierge',
      en: 'Concierge',
      point: 'Primary focus: responding to requests and arranging',
      text: 'A concierge excels at responsive expertise—returning optimal information and arrangements to requests that arrive.\n\nA butler’s core is anticipatory work grounded in daily observation, and continuity built through long-term trust and accumulated household context.\n\nOne-off arranging power and the integrative capacity to walk alongside a life are complementary, not identical.',
    },
  ];

  const levels = [
    {
      tier: 'I',
      name: 'Associate Butler',
      href: '/en/qualifications/associate-butler',
      img: '/images/generated/jba-become-foundation-training',
      alt: 'Foundation training at the Associate Butler stage: learning the basics of protocol and service',
      lead: 'An entry attainment: posture, protocol, and the observing eye that form the base',
      text: 'Standing, speech, confidentiality, and fundamental service procedures are learned as a system.\n\nBefore entering the field, this stage settles—in both body and awareness—what the profession values, and builds the ground on which later specialization rests.',
    },
    {
      tier: 'II',
      name: 'Professional Butler',
      href: '/en/qualifications/professional-butler',
      img: '/images/generated/jba-become-professional-practice',
      alt: 'Professional practice at the Professional Butler stage: applied work in field-like settings',
      lead: 'A full professional standard: able to run household operations in practice',
      text: 'Guest reception, schedule coordination, vendor management, table and space preparation—the breadth of practice widens, and service can be designed and delivered under situational judgment.\n\nWhat is required is reproducible skill in reading the principal’s intent and giving it form without excess or shortfall.',
    },
    {
      tier: 'III',
      name: 'Master Butler',
      href: '/en/qualifications/master-butler',
      img: '/images/generated/jba-butler-quality-control',
      alt: 'Quality oversight at the Master Butler stage: leadership, guidance, and standards in practice',
      lead: 'A mastery standard: final responsibility for oversight, development, and quality',
      text: 'This stage unifies multiple staff and external partners and oversees service quality across a household or residence.\n\nMentoring successors, deciding under difficult conditions, and sustaining long trust relationships all come into question—both the depth and the breadth of the profession.',
    },
  ];

  const misconceptions = [
    {
      q: 'Is a butler only a distant figure for the wealthy?',
      a: 'The glamorous image drawn by film and fiction is only one face of the role. As the Association defines it, a butler is a professional who steadies the foundations of daily life so the principal can turn toward what truly requires attention.\n\nScale matters less than how responsibility is held and how clearly attainment is stated.',
    },
    {
      q: 'Is this the same as domestic help or a secretary?',
      a: 'Skills overlap, yet the seat of responsibility differs. Domestic help centers on reliable execution of discrete tasks; a secretary on professional-side support; a butler on continuity of household operations and anticipation.\n\nWhen boundaries stay vague, both clients and practitioners struggle to align expectations.',
    },
    {
      q: 'Is practical experience enough without certification?',
      a: 'Experience is valuable, yet quality becomes hard to reproduce when it rests only on personal instinct. The Association’s attainment levels are a framework for showing skill and posture in a form third parties can understand.\n\nCertification is operated as a way to put experience into language and mark the path to the next stage.',
    },
    {
      q: 'Is this merely an imitation of Western tradition?',
      a: 'Since its founding in 1890, the Association has walked a path that joins British discipline with Japanese courtesy and hospitality.\n\nRather than importing a label, it defines the butler as a profession rooted in Japanese life, and continues training and certification on that basis.',
    },
  ];

  const faqJsonLd = [
    {
      question: 'How does the Japan Butler Association define a butler?',
      answer:
        'The Association defines a butler as a professional who integratively supports the household and the principal’s foundations of daily life, with anticipatory awareness and confidentiality at the core.\n\nThe role goes beyond substituting for discrete domestic tasks; it designs and sustains the continuity and dignity of living as a whole.',
    },
    {
      question: 'How does a butler differ from a housekeeper, secretary, or concierge?',
      answer:
        'Housekeepers and domestic help center on executing discrete household tasks; secretaries on professional schedules and documents; concierges on responding to requests and arranging.\n\nA butler centers on oversight of household operations, anticipation, and accompaniment grounded in long-term trust—a different layer of responsibility.',
    },
    {
      question: 'What attainment levels exist in the certification system?',
      answer:
        'Three core tiers—Associate Butler, Professional Butler, and Master Butler—are complemented by specialist qualifications in areas such as dining, travel, and business manner.\n\nThe structure allows progressive proof of capability from foundational posture through practice to oversight and development.',
    },
    {
      question: 'Has the butler profession taken root in Japan?',
      answer:
        'Since its introduction in the Meiji era, the profession has been carried forward while changing shape with society.\n\nThrough training, certification, and public information, the Association shares a definition of the role, clarifies misunderstandings, and continually updates where the butler profession stands in Japan today.',
    },
  ];

  const articleLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'What Is a Butler | Professional Definition and Standards of Attainment',
    description:
      'How the Japan Butler Association defines the butler profession: scope of responsibility, boundaries with adjacent roles, Associate, Professional, and Master attainment levels, and the profession’s place in Japan today.',
    author: {
      '@type': 'Organization',
      name: 'Japan Butler Association',
      foundingDate: '1890',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Japan Butler Association',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': 'https://butler-association.jp/en/butler',
    },
    inLanguage: 'en',
  };

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://butler-association.jp/en',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'What Is a Butler',
        item: 'https://butler-association.jp/en/butler',
      },
    ],
  };

  const faqLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqJsonLd.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer.replace(/\n{2,}/g, ' '),
      },
    })),
  };

  return (
    <Layout>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqLd) }} />

      <section className="relative bg-[#1a2942] overflow-hidden min-h-[520px] lg:min-h-[600px] flex items-center">
        <picture>
          <source srcSet="/images/generated/jba-butler-profession-hero.webp" type="image/webp" />
          <img
            src="/images/generated/jba-butler-profession-hero.jpg"
            alt="A quiet scene that evokes the butler profession: composed posture and dignified service"
            className="absolute inset-0 w-full h-full object-cover object-center"
          />
        </picture>
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(16,20,26,0.92)_0%,rgba(16,20,26,0.72)_48%,rgba(16,20,26,0.35)_100%)]"></div>
        <div className="relative z-10 w-full max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 pt-32 pb-16 lg:pt-36 lg:pb-20">
          <div className="max-w-[720px]">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.32em] text-xs sm:text-[13px] text-[#d7b779] mb-4">
              The Butler Profession
            </span>
            <div className="w-12 h-px bg-[#a38e65] mb-6"></div>
            <h1 className="font-['Playfair_Display',serif] font-medium text-white text-[clamp(28px,3.4vw,44px)] leading-[1.35] tracking-[0.01em] mb-6">
              <span className="block">What Is a Butler</span>
              <span className="block text-[#d7b779]">A professional definition</span>
            </h1>
            <div className="space-y-5">
              <p className="font-['Playfair_Display',serif] text-base sm:text-lg text-white/90 leading-[1.95]">
                The Japan Butler Association, as a certifying body, defines the butler not merely as someone who performs domestic tasks, but as a specialist who integratively supports the household and the principal’s foundations of daily life.
              </p>
              <p className="font-['Playfair_Display',serif] text-base sm:text-lg text-white/90 leading-[1.95]">
                This page sets out the scope of responsibility, boundaries with adjacent professions, the Association’s standards of attainment, and where the role stands in Japan today.
              </p>
            </div>
          </div>
        </div>
      </section>

      <div className="bg-white [&>div]:!pt-6">
        <Breadcrumb items={breadcrumbs} />
      </div>

      <div className="bg-white">
        <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16 lg:py-20 space-y-16 sm:space-y-20">

          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">I. Definition</span>
            <h2 className="font-['Playfair_Display',serif] font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.01em] mb-5">
              Defining the butler <span className="text-[#a38e65]">as a profession</span>
            </h2>
            <div className="space-y-[18px] font-['Playfair_Display',serif] text-base text-[#333] leading-[1.95]">
              <div className="space-y-5">
                <p>
                  When the Association uses the word “butler,” it does not begin from the costumes or grandeur that film and fiction may suggest.
                </p>
                <p>
                  At the center is a professional responsibility: to enter the interior of a principal’s household and keep steady the foundations of living and human relation. Completing individual tasks is not the aim in itself.
                </p>
                <p>
                  The focus is designing and sustaining the priorities, dignity, and continuity that sit behind those tasks.
                </p>
              </div>
              <div className="space-y-5">
                <p>
                  The Association places the butler at the meeting point of a European discipline of service and Japan’s long-standing spirit of hospitality.
                </p>
                <p>
                  Moving before one is asked; holding the necessary standard without self-display; never carrying outward what has been seen or heard. These are skills, and they are ethics as well.
                </p>
              </div>
              <div className="space-y-5">
                <p>
                  Under this definition, a butler is not “convenient extra hands.” The role is a profession that answers for the operating quality of the household as a small society, and that returns the principal’s time and attention to what they ought to face.
                </p>
                <p>
                  Matters of how to engage services or fee structures belong to service guides elsewhere; here we treat only the outline of the profession itself.
                </p>
              </div>
            </div>
            <dl className="grid grid-cols-2 md:grid-cols-4 gap-y-6 gap-x-4 mt-10 border-t border-[#e2ddd2] pt-8">
              <div>
                <dt className="font-['Playfair_Display',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1.5">Stance</dt>
                <dd className="font-['Playfair_Display',serif] text-lg text-[#1a2942] leading-tight pt-1">Certifying body</dd>
              </div>
              <div>
                <dt className="font-['Playfair_Display',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1.5">Focus</dt>
                <dd className="font-['Playfair_Display',serif] text-lg text-[#1a2942] leading-tight pt-1">Definition</dd>
              </div>
              <div>
                <dt className="font-['Playfair_Display',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1.5">Attainment</dt>
                <dd className="font-['Playfair_Display',serif] text-lg text-[#1a2942] leading-tight pt-1">Three tiers</dd>
              </div>
              <div>
                <dt className="font-['Playfair_Display',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1.5">Lineage</dt>
                <dd className="font-['Playfair_Display',serif] text-2xl text-[#1a2942]">1890</dd>
              </div>
            </dl>
          </section>

          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">II. Scope of Responsibility</span>
            <h2 className="font-['Playfair_Display',serif] font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.01em] mb-3">
              The <span className="text-[#a38e65]">scope of responsibility</span>
            </h2>
            <div className="space-y-5 mb-9">
              <p className="font-['Playfair_Display',serif] text-base text-[#333] leading-[1.95]">
                A butler’s responsibility cannot be measured only by reports that visible tasks are done. It is the making of a state in which the household continues, tomorrow and next month, at the same standard of dignity.
              </p>
              <p className="font-['Playfair_Display',serif] text-base text-[#333] leading-[1.95]">
                For that reason the Association regards the following four domains as professional core.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-x-10 gap-y-8">
              {responsibilityAreas.map((f) => (
                <div key={f.no} className="flex gap-5 border-t border-[#e2ddd2] pt-6">
                  <span className="font-['Playfair_Display',serif] text-[#d7b779] text-3xl leading-none shrink-0 pt-1">{f.no}</span>
                  <div>
                    <span className="block font-['Playfair_Display',serif] italic text-[#8f7a56] text-[13px] tracking-[0.06em] mb-1">{f.en}</span>
                    <h3 className="font-['Playfair_Display',serif] font-medium text-[#1a2942] text-lg sm:text-xl mb-2.5 leading-[1.5]">{f.title}</h3>
                    <div className="space-y-4">{f.desc.split(/\n{2,}/).map((s, i) => (
                      <p key={i} className="font-['Playfair_Display',serif] text-sm text-[#4a4638] leading-[1.9]">{s.trim()}</p>
                    ))}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-10 grid lg:grid-cols-[1fr_320px] gap-8 lg:gap-12 items-start">
              <div className="font-['Playfair_Display',serif] text-base text-[#333] leading-[1.95] space-y-[18px]">
                <div className="space-y-5">
                  <p>
                    The outer edge of responsibility matters as well. Medical acts, legal agency, and substituting for the principal’s own decisions do not belong to the butler’s profession.
                  </p>
                  <p>
                    The stance is to prepare the environment in the domain of living and interpersonal service, always on the premise of the principal’s will. Knowing that boundary is itself, in the Association’s view, a condition of professionalism.
                  </p>
                </div>
                <div className="space-y-5">
                  <p>
                    Responsibility is also not left to personal zeal alone. Only with standardized procedures, habits of review, and guidance from those at higher attainment does quality rise from individual “attentiveness” into a profession.
                  </p>
                  <p>
                    The Association’s training and certification make that structure visible to society as well.
                  </p>
                </div>
              </div>
              <div className="lg:pt-1">
                <picture>
                  <source srcSet="/images/generated/jba-butler-team-briefing.webp" type="image/webp" />
                  <img
                    src="/images/generated/jba-butler-team-briefing.jpg"
                    alt="A butler team briefing: confirming responsibilities and the day’s sequence"
                    className="w-full h-56 lg:h-[320px] object-cover border border-[#a38e65]/30"
                  />
                </picture>
              </div>
            </div>
          </section>

          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">III. Professional Boundaries</span>
            <h2 className="font-['Playfair_Display',serif] font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.01em] mb-3">
              Boundaries with <span className="text-[#a38e65]">adjacent professions</span>
            </h2>
            <div className="space-y-5 mb-9">
              <p className="font-['Playfair_Display',serif] text-base text-[#333] leading-[1.95]">
                Housekeepers and domestic help, secretaries, and concierges are each highly specialized roles. The difference from a butler is not a ranking of worth; it lies in the seat of responsibility and in how time is held.
              </p>
              <p className="font-['Playfair_Display',serif] text-base text-[#333] leading-[1.95]">
                Putting the boundary into words makes it easier for trainers and for society to align expectations.
              </p>
            </div>
            <div className="space-y-5">
              {boundaries.map((b) => (
                <div key={b.role} className="bg-white border border-[#e2ddd2] p-6 sm:p-8 shadow-[0_1px_0_rgba(26,41,66,0.04)]">
                  <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 mb-3">
                    <h3 className="font-['Playfair_Display',serif] font-medium text-[#1a2942] text-xl leading-[1.5]">{b.role}</h3>
                    <span className="font-['Playfair_Display',serif] italic text-[#8f7a56] text-sm tracking-[0.06em]">{b.en}</span>
                  </div>
                  <div className="flex items-center gap-3 mb-4">
                    <span className="w-6 h-px bg-[#a38e65]"></span>
                    <span className="font-['Playfair_Display',serif] text-sm text-[#a38e65] tracking-[0.04em]">{b.point}</span>
                  </div>
                  <div className="space-y-4">{b.text.split(/\n{2,}/).map((s, i) => (
                    <p key={i} className="font-['Playfair_Display',serif] text-[15px] text-[#4a4638] leading-[1.95]">{s.trim()}</p>
                  ))}</div>
                </div>
              ))}
            </div>
            <div className="mt-10 grid lg:grid-cols-[320px_1fr] gap-8 lg:gap-12 items-start">
              <picture>
                <source srcSet="/images/generated/jba-become-foundation-training.webp" type="image/webp" />
                <img
                  src="/images/generated/jba-become-foundation-training.jpg"
                  alt="Foundation training that respects professional boundaries: protocol and role clarity"
                  className="w-full h-56 lg:h-[320px] object-cover border border-[#a38e65]/30"
                />
              </picture>
              <div className="space-y-5 lg:pt-1">
                <p className="font-['Playfair_Display',serif] text-base text-[#333] leading-[1.95]">
                  In the field, one person may carry more than one function. Even so, the Association’s education and certification deliberately separate how far the butler’s own domain of judgment extends.
                </p>
                <p className="font-['Playfair_Display',serif] text-base text-[#333] leading-[1.95]">
                  Concurrent duty is a reality; conflation is an abandonment of definition—and that distinction is the first step in protecting the profession. Those who wish to pursue the path can also read how to begin on our page about{' '}
                  <a href="/en/butler/become/" className="text-[#1a2942] border-b border-[#a38e65] hover:text-[#a38e65] transition-colors">becoming a butler</a>.
                </p>
              </div>
            </div>
          </section>

          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">IV. Standards of Attainment</span>
            <h2 className="font-['Playfair_Display',serif] font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.01em] mb-3">
              Standards of attainment <span className="text-[#a38e65]">set by the Association</span>
            </h2>
            <div className="space-y-5 mb-10">
              <p className="font-['Playfair_Display',serif] text-base text-[#333] leading-[1.95]">
                The Association operates three tiers—Associate, Professional, and Master—as standards of attainment for the butler profession.
              </p>
              <p className="font-['Playfair_Display',serif] text-base text-[#333] leading-[1.95]">
                Specialist qualifications in areas such as dining, travel, and business manner further allow depth in particular domains to be demonstrated.
              </p>
              <p className="font-['Playfair_Display',serif] text-base text-[#333] leading-[1.95]">
                What follows is a definitional account of what each tier can carry; it is not an application guide or a schedule of fees.
              </p>
            </div>

            <div className="space-y-0">
              {levels.map((lv, idx) => (
                <article key={lv.tier} className="border-t border-[#e2ddd2] first:border-t-0">
                  <div className="bg-[#1a2942] px-5 sm:px-7 py-4 flex flex-wrap items-center gap-x-5 gap-y-2">
                    <span className="font-['Playfair_Display',serif] text-[#d7b779] text-2xl sm:text-3xl leading-none tracking-[0.06em]">{lv.tier}</span>
                    <div className="w-px h-6 bg-[#a38e65]/50 hidden sm:block"></div>
                    <div>
                      <span className="block font-['Playfair_Display',serif] italic text-[#a38e65] text-[12px] tracking-[0.12em]">Tier {lv.tier}</span>
                      <h3 className="font-['Playfair_Display',serif] font-medium text-[#f8f6f0] text-lg sm:text-xl leading-[1.4]">{lv.name}</h3>
                    </div>
                  </div>
                  <div className={`grid lg:grid-cols-2 gap-0 ${idx % 2 === 1 ? 'lg:[&>div:first-child]:order-2' : ''}`}>
                    <div className="p-6 sm:p-8 lg:p-10 flex flex-col justify-center bg-[#f8f6f0]/40">
                      <p className="font-['Playfair_Display',serif] text-sm text-[#a38e65] tracking-[0.04em] mb-3">{lv.lead}</p>
                      <div className="space-y-4 mb-6">{lv.text.split(/\n{2,}/).map((s, i) => (
                        <p key={i} className="font-['Playfair_Display',serif] text-[15px] text-[#333] leading-[1.95]">{s.trim()}</p>
                      ))}</div>
                      <a href={lv.href} className={`${BTN08_ON_LIGHT} self-start`}>View tier details</a>
                    </div>
                    <div className="min-h-[240px] lg:min-h-[320px]">
                      <picture>
                        <source srcSet={`${lv.img}.webp`} type="image/webp" />
                        <img
                          src={`${lv.img}.jpg`}
                          alt={lv.alt}
                          className="w-full h-full min-h-[240px] lg:min-h-[320px] object-cover"
                        />
                      </picture>
                    </div>
                  </div>
                </article>
              ))}
            </div>

            <div className="mt-10 border border-[#e2ddd2] bg-white p-6 sm:p-8">
              <div className="space-y-5">
                <p className="font-['Playfair_Display',serif] text-[15px] text-[#4a4638] leading-[1.95]">
                  The full picture of the three tiers and the place of specialist qualifications is summarized in our{' '}
                  <a href="/en/qualifications/" className="text-[#1a2942] border-b border-[#a38e65] hover:text-[#a38e65] transition-colors">certification overview</a>.
                </p>
                <p className="font-['Playfair_Display',serif] text-[15px] text-[#4a4638] leading-[1.95]">
                  Each level is not a mere title; it is a shared language for showing, step by step, the quality of observation, judgment, execution, and oversight.
                </p>
              </div>
            </div>
          </section>

          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">V. Quality Discipline</span>
            <h2 className="font-['Playfair_Display',serif] font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.01em] mb-6">
              Holding quality <span className="text-[#a38e65]">as a profession</span>
            </h2>
            <div className="grid lg:grid-cols-[320px_1fr] gap-8 lg:gap-12 items-start">
              <picture>
                <source srcSet="/images/generated/jba-butler-quality-control.webp" type="image/webp" />
                <img
                  src="/images/generated/jba-butler-quality-control.jpg"
                  alt="Quality control in butler service: attention to detail and consistent standards"
                  className="w-full h-56 lg:h-[340px] object-cover border border-[#a38e65]/30"
                />
              </picture>
              <div className="space-y-[18px] font-['Playfair_Display',serif] text-base text-[#333] leading-[1.95]">
                <div className="space-y-5">
                  <p>
                    Attainment levels exist not to multiply examinations for their own sake.
                  </p>
                  <p>
                    Precisely because the interior of a household is hard to verify from outside, practitioners themselves need a calm measure of which layer of judgment they are presently trusted to hold. That is the purpose of the scale.
                  </p>
                </div>
                <div className="space-y-5">
                  <p>
                    Quality management does not end in clearing a checklist. Even with the same procedure, the best answer shifts with the principal’s condition, a guest’s temperament, or the air of the season.
                  </p>
                  <p>
                    To master the standard, and then to judge when to depart from it on purpose—the Association’s training values this back-and-forth.
                  </p>
                </div>
                <div className="space-y-5">
                  <p>
                    Under Representative Director Yuichi Ishii, the Association advances both the inheritance of tradition and adaptation to contemporary ways of living.
                  </p>
                  <p>
                    Definition and practice are kept together so that quality remains a professional discipline rather than a private knack.
                  </p>
                </div>
              </div>
            </div>
          </section>

          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">VI. In Japan Today</span>
            <h2 className="font-['Playfair_Display',serif] font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.01em] mb-5">
              Where the butler profession <span className="text-[#a38e65]">stands in Japan</span>
            </h2>
            <div className="grid lg:grid-cols-[1fr_320px] gap-8 lg:gap-12 items-start">
              <div className="space-y-[18px] font-['Playfair_Display',serif] text-base text-[#333] leading-[1.95]">
                <div className="space-y-5">
                  <p>
                    Since the Association’s founding in 1890, the butler profession in Japan has changed shape with society. The classical image of residence in a great house is not the only correct form.
                  </p>
                  <p>
                    Today, modes of oversight and density of involvement vary with household scale and lifestyle. What matters is not outward grandeur, but whether the way responsibility is taken can be explained as a profession.
                  </p>
                </div>
                <div className="space-y-5">
                  <p>
                    At the same time, the title sometimes runs ahead of a shared definition, and “butler-like” services are described without a settled core.
                  </p>
                  <p>
                    As a certifying body, the Association treats it as one of its missions to fix the meaning of the word through curriculum and attainment levels, so that learners can refer to the same map.
                  </p>
                </div>
                <div className="space-y-5">
                  <p>
                    Japanese courtesy, a sense of season, and the structure of dwellings differ from the conditions of classical Western estate management.
                  </p>
                  <p>
                    For that reason it is not enough to set down an imported term as-is; the work of translating the role into a profession that functions in Japanese life must continue.
                  </p>
                  <p>
                    Training, certification, seminars, international exchange, and publishing—outlined in the Association overview—are pillars of that translation and settlement.
                  </p>
                </div>
                <div className="space-y-5">
                  <p>
                    In a single phrase, the present position is this: recognition is widening, yet shared definition is still underway.
                  </p>
                  <p>
                    That is why, apart from client-facing explanations, a page that draws the profession’s outline from the certifying side remains necessary. This page carries that role.
                  </p>
                </div>
              </div>
              <div className="lg:pt-1">
                <picture>
                  <source srcSet="/images/generated/jba-become-professional-practice.webp" type="image/webp" />
                  <img
                    src="/images/generated/jba-become-professional-practice.jpg"
                    alt="Butler practice rooted in Japanese daily life: professional support for modern households"
                    className="w-full h-56 lg:h-[340px] object-cover border border-[#a38e65]/30"
                  />
                </picture>
              </div>
            </div>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <a href="/en/about/overview/" className={BTN08_ON_LIGHT}>Read the Association overview</a>
              <a href="/en/butler-services/" className={BTN08_ON_LIGHT}>How we think about butler services</a>
            </div>
          </section>

          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">VII. Misconceptions</span>
            <h2 className="font-['Playfair_Display',serif] font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.01em] mb-6">
              Clearing up <span className="text-[#a38e65]">common misconceptions</span>
            </h2>
            <div className="space-y-0 border-t-2 border-[#1a2942]">
              {misconceptions.map((m, i) => (
                <div key={i} className="border-b border-[#e2ddd2] py-6 sm:py-7">
                  <div className="flex gap-4 sm:gap-6">
                    <span className="font-['Playfair_Display',serif] text-[#d7b779] text-xl shrink-0 pt-0.5">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <div>
                      <h3 className="font-['Playfair_Display',serif] font-medium text-[#1a2942] text-lg sm:text-xl mb-3 leading-[1.5]">{m.q}</h3>
                      <div className="space-y-4">{m.a.split(/\n{2,}/).map((s, i) => (
                        <p key={i} className="font-['Playfair_Display',serif] text-[15px] text-[#4a4638] leading-[1.95]">{s.trim()}</p>
                      ))}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">VIII. FAQ</span>
            <h2 className="font-['Playfair_Display',serif] font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.01em] mb-6">
              Questions and answers on <span className="text-[#a38e65]">professional definition</span>
            </h2>
            <div className="space-y-4">
              {faqJsonLd.map((item) => (
                <details key={item.question} className="group border border-[#e2ddd2] bg-white open:bg-[#f8f6f0]/35 transition-colors">
                  <summary className="cursor-pointer list-none flex items-start gap-4 p-5 sm:p-6 font-['Playfair_Display',serif] text-[15px] sm:text-base text-[#1a2942] leading-[1.7]">
                    <span className="font-['Playfair_Display',serif] text-[#a38e65] shrink-0 mt-0.5">Q</span>
                    <span className="flex-1">{item.question}</span>
                    <span className="text-[#a38e65] shrink-0 group-open:rotate-45 transition-transform text-xl leading-none">+</span>
                  </summary>
                  <div className="px-5 sm:px-6 pb-5 sm:pb-6 pl-12 sm:pl-14">
                    <div className="space-y-4">{item.answer.split(/\n{2,}/).map((s, i) => (
                      <p key={i} className="font-['Playfair_Display',serif] text-sm sm:text-[15px] text-[#4a4638] leading-[1.95]">{s.trim()}</p>
                    ))}</div>
                  </div>
                </details>
              ))}
            </div>
          </section>

          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">IX. Next Steps</span>
            <h2 className="font-['Playfair_Display',serif] font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.01em] mb-6">
              Beyond the definition
            </h2>
            <div className="bg-[#1a2942] border border-[#a38e65]/45 p-8 sm:p-10 lg:p-12">
              <div className="space-y-5 mb-8">
                <p className="font-['Playfair_Display',serif] text-base sm:text-lg text-[#e7e2d8] leading-[1.95] max-w-3xl">
                  Once the butler is understood as a profession, you may move on to the detail of attainment levels, the Association’s formation, or service as a practical expression of the role.
                </p>
                <p className="font-['Playfair_Display',serif] text-base sm:text-lg text-[#e7e2d8] leading-[1.95] max-w-3xl">
                  Definition and the work of training and certification form one continuum. Choose the entrance that matches your interest and continue reading.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 flex-wrap">
                <a href="/en/qualifications/" className={BTN08_ON_NAVY}>Explore certifications</a>
                <a href="/en/butler/become/" className={BTN08_ON_NAVY}>How to become a butler</a>
                <a href="/en/about/overview/" className={BTN08_ON_NAVY}>Association overview</a>
              </div>
            </div>
          </section>

        </div>
      </div>
    </Layout>
  );
}
